/**
 * Utility to break up long synchronous tasks on the main thread
 * by deferring non-critical work to idle time
 */

type IdleCallback = () => void;

/**
 * Schedule a callback to run during idle time
 * Falls back to setTimeout if requestIdleCallback is not available
 */
export const scheduleIdleTask = (callback: IdleCallback): void => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(callback, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(callback, 1);
  }
};

/**
 * Break up a large array processing task into smaller chunks
 * to avoid blocking the main thread
 */
export const processInChunks = <T>(
  items: T[],
  processItem: (item: T) => void,
  chunkSize: number = 10
): Promise<void> => {
  return new Promise((resolve) => {
    let index = 0;

    const processChunk = () => {
      const endIndex = Math.min(index + chunkSize, items.length);
      
      for (let i = index; i < endIndex; i++) {
        processItem(items[i]);
      }

      index = endIndex;

      if (index < items.length) {
        scheduleIdleTask(processChunk);
      } else {
        resolve();
      }
    };

    scheduleIdleTask(processChunk);
  });
};

/**
 * Defer execution until after paint
 */
export const deferUntilPaint = (callback: IdleCallback): void => {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
};
